import { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { refresh } from './features/Auth/repositories/auth-repository'
import { axios, cleanAxios, serverHost } from './http-clients'
import i18n from './i18n'
import store from './redux'
import { updateUser } from './redux/slices/user'
import { forwardAccessTokenToPspWidget } from './utils/psp-widget-auth-proof'
import { type PlatformLanguage } from './utils/types/i18n'

axios.interceptors.request.use(
  config => {
    const { user } = store.getState()

    if (user?.token) {
      config.headers.Authorization = `Bearer ${user.token}`
    }

    return config
  },
  async error => await Promise.reject(error),
)

axios.interceptors.request.use(
  config => {
    if (!config.headers['X-locale']) {
      config.headers['X-locale'] = mapToBackendLanguageWithFallback(
        i18n.language as PlatformLanguage,
      )
    }

    return config
  },
  async error => await Promise.reject(error),
)

interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _isRetry?: boolean
}

interface RetryableAxiosError extends AxiosError {
  config: RetryableAxiosRequestConfig
}

interface ActiveRefresh {
  sessionKey: string
  promise: ReturnType<typeof refresh>
}

let activeRefresh: ActiveRefresh | null = null

function shouldInvalidateSession(error: unknown) {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return false
  }

  const response = error.response
  if (
    typeof response !== 'object' ||
    response === null ||
    !('status' in response)
  ) {
    return false
  }

  return (
    response.status === 400 ||
    response.status === 401 ||
    response.status === 403
  )
}

/**
 * Refreshes the current session, persists rotated credentials and forwards
 * the exact new access token to the PSP widget. Concurrent callers share one
 * refresh request so a rotating refresh token is never used twice.
 */
export async function refreshAccessToken(): Promise<string> {
  const { user } = store.getState()

  if (!user?.refreshToken || !user?.username) {
    throw new Error('Cannot refresh a session without refresh credentials')
  }

  const sessionKey = `${user.username}:${user.refreshToken}`

  if (!activeRefresh || activeRefresh.sessionKey !== sessionKey) {
    const refreshToken = user.refreshToken
    const username = user.username

    const refreshRequest = refresh({
      refreshToken,
      userName: username,
    }).then(response => {
      const currentUser = store.getState().user

      if (
        currentUser?.username === username &&
        currentUser.refreshToken === refreshToken
      ) {
        const newAccessToken = response.data.access_token?.trim()
        if (!newAccessToken) {
          throw new Error('Refresh response did not include an access token')
        }

        const rotatedRefreshToken = response.data.refresh_token?.trim()
        let nextRefreshToken = refreshToken
        if (rotatedRefreshToken) nextRefreshToken = rotatedRefreshToken

        store.dispatch(
          updateUser({
            ...currentUser,
            token: newAccessToken,
            refreshToken: nextRefreshToken,
            refreshTokenExpiresAt:
              response.data.refresh_token_expires_at ??
              currentUser.refreshTokenExpiresAt,
          }),
        )

        forwardAccessTokenToPspWidget(newAccessToken)
      }

      return response
    })

    const trackedRefresh = refreshRequest.finally(() => {
      if (activeRefresh?.promise === trackedRefresh) {
        activeRefresh = null
      }
    })

    activeRefresh = { sessionKey, promise: trackedRefresh }
  }

  const response = await activeRefresh.promise.catch(refreshError => {
    const currentUser = store.getState().user

    if (
      shouldInvalidateSession(refreshError) &&
      currentUser?.username === user.username &&
      currentUser.refreshToken === user.refreshToken
    ) {
      store.dispatch(updateUser(null))
    }

    throw refreshError
  })

  const currentUser = store.getState().user
  if (!currentUser || currentUser.token !== response.data.access_token) {
    throw new Error('The session changed while refreshing its access token')
  }

  return response.data.access_token
}

axios.interceptors.response.use(
  response => response,
  async (error: RetryableAxiosError) => {
    if (error.response?.status !== 401) throw error

    if (error.config._isRetry) {
      throw error
    }

    const { user } = store.getState()

    if (!user?.refreshToken || !user?.username) {
      store.dispatch(updateUser(null))
      throw error
    }

    error.config._isRetry = true

    const currentAuthorization = `Bearer ${user.token}`
    if (error.config.headers.Authorization !== currentAuthorization) {
      error.config.headers.Authorization = currentAuthorization
      return await axios(error.config)
    }

    let refreshedToken

    try {
      refreshedToken = await refreshAccessToken()
    } catch {
      throw error
    }

    error.config.headers.Authorization = `Bearer ${refreshedToken}`
    return await axios(error.config)
  },
)

export { axios, cleanAxios, serverHost }

export function normalizeToPrimaryLanguages(language: PlatformLanguage) {
  return language === 'en' ? 'en_US' : 'es_PY'
}

export function mapToBackendLanguageWithFallback(language: PlatformLanguage) {
  type Languages = {
    [key in PlatformLanguage]: string
  }
  const languages = {
    en: 'en_US',
    es: 'es_PY',
    pt: 'pt_PT',
    'pt-BR': 'pt_BR',
    ht: 'ht_HT',
    sk: 'sk_SK',
    bg: 'bg_BG',
    ro: 'ro_RO',
    ar: 'ar_AR',
  } as const satisfies Languages

  return languages[language] || languages.en
}

export function mapToFrontendLanguageWithFallback(language: string) {
  type Languages = {
    [key in string]: PlatformLanguage
  }
  const languages: Languages = {
    en_US: 'en',
    es_PY: 'es',
    pt_BR: 'pt-BR',
    pt_PT: 'pt',
    ht_HT: 'ht',
    sk_SK: 'sk',
    bg_BG: 'bg',
    ro_RO: 'ro',
    ar_AR: 'ar',
  } as const

  return languages[language] || 'en'
}

/**
 * This interface is used as a generic type for axios' responses
 */
export interface ServerResponse<T> {
  data: T
  errors: any[]
  dataPresent: boolean
  extensions: null
  /** HTTP-style error code returned by the server in the response body (e.g. 400, 403, 500). Present when the backend signals an error via the body instead of the HTTP status. */
  code?: number
}
